import * as React from "react"
import { useState, useEffect } from "react"
import { motion, useMotionValue, useSpring } from "framer-motion"
import { useFinePointer } from "./useFinePointer"

// Smooth follow: the pointer position is written straight to motion values in
// the event handler (no React re-render per move — that was the source of the
// jitter), then a spring eases the circle toward the pointer. The position
// layer (.cursor-track) and the appearance layer (.circle) are separate, so
// resizing the circle never competes with the follow transform.
const FOLLOW_SPRING = { stiffness: 500, damping: 38, mass: 0.55 }

function Cursor(props) {
  const [mounted, setMounted] = useState(false)
  const finePointer = useFinePointer()

  const x = useMotionValue(-100)
  const y = useMotionValue(-100)
  const springX = useSpring(x, FOLLOW_SPRING)
  const springY = useSpring(y, FOLLOW_SPRING)

  useEffect(() => {
    setMounted(true)
  }, [])

  useEffect(() => {
    if (!mounted || !finePointer) return undefined
    const onMove = (event) => {
      x.set(event.clientX)
      y.set(event.clientY)
    }
    window.addEventListener("mousemove", onMove, { passive: true })
    return () => window.removeEventListener("mousemove", onMove)
  }, [mounted, finePointer, x, y])

  // Decorative, pointer-only: render nothing during SSR / first client render,
  // and nothing on touch / coarse-pointer / no-hover devices.
  if (!mounted || !finePointer) return null

  const variants = {
    initial: {
      opacity: 0,
      height: 10,
      width: 10,
      fontSize: "16px",
      backgroundColor: "rgba(0, 0, 0, 0)",
    },
    default: {
      opacity: 0,
      height: 10,
      width: 10,
      fontSize: "16px",
      backgroundColor: "rgba(154, 184, 218, 1)",
    },
    project: {
      opacity: 1,
      height: 80,
      width: 80,
      fontSize: "18px",
      color: "#000",
      backgroundColor: "rgba(255, 255, 255, 0.9)",
    },
    contact: {
      opacity: 1,
      height: 64,
      width: 64,
      fontSize: "32px",
      color: "#000",
      backgroundColor: "rgba(247, 164, 172, 0.85)",
    },
    link: {
      opacity: 1,
      height: 80,
      width: 80,
      fontSize: "32px",
      color: "#000",
      backgroundColor: "rgba(3, 77, 162, 0.1)",
    },
  }

  return (
    <motion.div
      className="cursor-track"
      style={{ x: springX, y: springY }}
      aria-hidden="true"
    >
      <motion.div
        className="circle"
        variants={variants}
        initial="initial"
        animate={props.cursorVariant}
        transition={{ type: "spring", stiffness: 300, damping: 26, mass: 0.5 }}
      >
        <span className="cursor-text">{props.cursorText}</span>
      </motion.div>
    </motion.div>
  )
}

export default Cursor
